'use client'

import Link from 'next/link'
import { motion } from '@/lib/framer-motion-shim'

interface CTASectionProps {
  badge?: string
  title: string
  subtitle?: string
  primaryCTA?: { label: string; href: string }
  secondaryCTA?: { label: string; href: string }
  variant?: 'forest' | 'light' | 'sage'
}

export default function CTASection({
  badge,
  title,
  subtitle,
  primaryCTA = { label: 'Nous contacter', href: '/contact' },
  secondaryCTA,
  variant = 'forest',
}: CTASectionProps) {
  const bgClass =
    variant === 'forest'
      ? 'gradient-forest text-white'
      : variant === 'sage'
      ? 'bg-sage'
      : 'bg-white'

  return (
    <section className={`section-padding ${bgClass} relative overflow-hidden`}>
      {/* Decorative circles */}
      {variant === 'forest' && (
        <>
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full border border-white/[0.04]" />
          <div className="absolute -bottom-32 -left-16 w-64 h-64 rounded-full border border-white/[0.04]" />
        </>
      )}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        className="container-site text-center max-w-3xl mx-auto relative z-10"
      >
        {badge && (
          <span
            className={`inline-block font-heading font-semibold text-xs uppercase tracking-widest mb-4 px-4 py-1.5 rounded-full ${
              variant === 'forest' ? 'bg-white/10 text-amber' : 'bg-forest/10 text-forest'
            }`}
          >
            {badge}
          </span>
        )}
        <h2
          className={`font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-balance ${
            variant === 'forest' ? 'text-white' : 'text-gray-900'
          }`}
        >
          {title}
        </h2>
        {subtitle && (
          <p
            className={`mt-4 text-lg md:text-xl ${
              variant === 'forest' ? 'text-white/70' : 'text-gray-500'
            }`}
          >
            {subtitle}
          </p>
        )}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
          <Link href={primaryCTA.href} className="btn-primary">
            {primaryCTA.label}
          </Link>
          {secondaryCTA && (
            <Link
              href={secondaryCTA.href}
              className={`font-heading font-semibold text-sm transition-colors ${
                variant === 'forest'
                  ? 'text-white/80 hover:text-white'
                  : 'text-forest hover:text-forest-light'
              }`}
            >
              {secondaryCTA.label} →
            </Link>
          )}
        </div>
      </motion.div>
    </section>
  )
}
